/**
 * Trust Certificate — LIVE demo walkthrough page. Not part of the consumer
 * wallet IA. This is a pitch/marketing page.
 *
 * A single "Run demo" button seeds a real consumer + real journey + real
 * signed Trust Certificate end-to-end via /api/demo/trust-certificate-scenario.
 * Everything the page then links to is a real running artefact — perfect for
 * a live demo where nothing is faked.
 */
import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Sparkles, Loader2, Copy, ExternalLink, ShieldCheck, FileText, Upload,
  KeyRound, ArrowRight, Building2, User, CheckCircle2, RefreshCcw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

export default function TrustCertificateDemoPage() {
  const [scenario, setScenario] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const run = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/demo/trust-certificate-scenario`, {});
      setScenario(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to seed demo scenario");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen" style={{ background: "#F8F9FA" }}>
      {/* Hero */}
      <header className="text-white" style={{ background: `linear-gradient(135deg, ${NAVY} 0%, #061633 100%)` }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center font-display font-bold text-white" style={{ background: GOLD, color: NAVY }}>A</div>
            <div className="leading-tight">
              <div className="text-[13px] font-semibold">Altara</div>
              <div className="text-[9px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Trust Certificate · Live demo</div>
            </div>
          </Link>
          <h1 className="font-display text-3xl lg:text-5xl font-semibold max-w-3xl">See the Altara Trust Certificate flow end-to-end.</h1>
          <p className="mt-4 text-sm lg:text-base text-white/80 max-w-2xl leading-relaxed">
            One click seeds a live scenario: an advisor at a fictional South African insurer sends a real 20-year Term Life quote through the Altara pipeline. A cryptographically-signed Trust Certificate is minted, and every link below is a real running artefact — nothing is mocked.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button
              onClick={run}
              disabled={busy}
              data-testid="demo-run"
              className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition disabled:opacity-40"
              style={{ background: GOLD, color: NAVY }}
            >
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Seeding scenario…</> : scenario ? <><RefreshCcw className="w-4 h-4" /> Run again with fresh IDs</> : <><Sparkles className="w-4 h-4" /> Run the demo scenario</>}
            </button>
            <a
              href={`${API}/demo/quote-pdf`}
              target="_blank"
              rel="noreferrer"
              data-testid="demo-download-quote"
              className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold transition border"
              style={{ borderColor: `${GOLD}55`, color: "white" }}
            >
              <FileText className="w-4 h-4" /> Download the demo quote (PDF)
            </a>
          </div>
          {error && <div className="mt-4 text-sm text-rose-300">{error}</div>}
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-12 lg:py-16 space-y-14">
        {/* Story pre-scenario */}
        {!scenario && <BeforeState />}

        {/* Live scenario */}
        {scenario && <AfterState scenario={scenario} />}

        {/* Always-on: how the trust certificate proves authenticity */}
        <HowItProves />
      </main>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-6xl mx-auto px-6 text-center text-[11px] text-slate-500">
          Aurora Life &amp; Insurance in this demo is a fictional South African financial services provider used only for illustration. All artefacts on this page are real live systems.
        </div>
      </footer>
    </div>
  );
}

function BeforeState() {
  const steps = [
    { n: 1, title: "The consumer meets their advisor", body: "Face-to-face, over WhatsApp, or on a call. The conversation happens outside Altara — where relationships actually live." },
    { n: 2, title: "The advisor prepares a quote", body: "A real PDF document is generated inside the institution's normal workflow (e.g. Sanlam iPlan, Discovery Compass, Aurora's system in this demo)." },
    { n: 3, title: "Consumer sends a secure drop-off link", body: "From inside their wallet, the consumer generates a short-lived link and shares it via WhatsApp / SMS / email." },
    { n: 4, title: "Advisor deposits the file", body: "The advisor opens the link, drops the file. No attachments, no email spoofing, no phishing surface." },
    { n: 5, title: "Altara mints a Trust Certificate", body: "Ed25519-signed proof of origin, integrity, timing and version. Available to consumer, advisor, and any compliance officer — offline verifiable, forever." },
  ];
  return (
    <section>
      <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>How the story unfolds</div>
      <h2 className="font-display text-2xl lg:text-3xl font-semibold mt-1" style={{ color: NAVY }}>Five steps from human conversation to cryptographic proof</h2>
      <ol className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {steps.map(s => (
          <li key={s.n} className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-sm font-bold" style={{ background: NAVY }}>{s.n}</div>
            <div className="mt-3 font-semibold text-sm" style={{ color: NAVY }}>{s.title}</div>
            <p className="mt-2 text-[12px] leading-relaxed text-slate-600">{s.body}</p>
          </li>
        ))}
      </ol>
    </section>
  );
}

function AfterState({ scenario }) {
  return (
    <section className="space-y-10" data-testid="demo-scenario">
      {/* Actors */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Live scenario · Scenario ID {scenario.scenario_id}</div>
        <h2 className="font-display text-2xl lg:text-3xl font-semibold mt-1" style={{ color: NAVY }}>Meet the people in this demo</h2>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <ActorCard icon={User} tone="navy" title={scenario.consumer.full_name} subtitle="Consumer" rows={[
            ["Email", scenario.consumer.email],
            ["Device ID", scenario.consumer.device_id],
          ]} />
          <ActorCard icon={Building2} tone="gold" title={`${scenario.advisor.name}`} subtitle={`Advisor · ${scenario.advisor.institution}`} rows={[
            ["Email", scenario.advisor.email],
            ["Institution (claimed)", scenario.advisor.institution],
          ]} />
        </div>
      </div>

      {/* The certificate */}
      <div className="rounded-3xl overflow-hidden border shadow-lg" style={{ borderColor: `${GOLD}55` }}>
        <div className="px-6 lg:px-10 py-6 text-white" style={{ background: NAVY }}>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>
            <ShieldCheck className="w-3.5 h-3.5" /> Altara Trust Certificate issued
          </div>
          <div className="mt-2 flex items-center justify-between gap-4 flex-wrap">
            <div className="font-display text-2xl lg:text-3xl font-semibold break-all" data-testid="demo-cert-id">{scenario.trust_certificate.certificate_id}</div>
            <a
              href={scenario.trust_certificate.verify_url}
              target="_blank"
              rel="noreferrer"
              data-testid="demo-verify-link"
              className="inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold"
              style={{ background: GOLD, color: NAVY }}
            >
              Open public verify page <ArrowRight className="w-4 h-4" />
            </a>
          </div>
        </div>
        <div className="p-6 lg:p-10 bg-white grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">File pinned by this certificate</div>
            <div className="mt-1 font-semibold" style={{ color: NAVY }}>{scenario.document.filename}</div>
            <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500">SHA-256 (integrity anchor)</div>
            <div className="mt-1 font-mono text-[11px] text-slate-700 break-all bg-slate-50 rounded-lg p-3">{scenario.document.sha256}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500">Envelope hash (feeds next revision)</div>
            <div className="mt-1 font-mono text-[11px] text-slate-700 break-all bg-slate-50 rounded-lg p-3">{scenario.trust_certificate.envelope_sha256}</div>
            <div className="mt-3 text-[10px] uppercase tracking-widest text-slate-500">Journey ID</div>
            <div className="mt-1 font-mono text-[11px] text-slate-700 break-all">{scenario.journey.journey_id}</div>
          </div>
        </div>
      </div>

      {/* Deep-links walk-through */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>Walk the demo</div>
        <h2 className="font-display text-2xl lg:text-3xl font-semibold mt-1" style={{ color: NAVY }}>Every link is live — click through in order</h2>
        <div className="mt-6 space-y-3">
          <DemoLink n={1} icon={FileText} url={scenario.demo_links.quote_pdf}
            title="The quote itself"
            body="This is the actual PDF the advisor prepared. Open it in a new tab to show what the consumer will receive." />
          <DemoLink n={2} icon={Upload} url={scenario.demo_links.advisor_drop_off_page}
            title="What the advisor sees when they open the drop-off link"
            body="The consumer-issued magic-link deposit page — no login, no email, no phishing surface. In a real demo, drop the quote PDF here." />
          <DemoLink n={3} icon={ShieldCheck} url={scenario.demo_links.consumer_journey_portal}
            title="The consumer's journey page"
            body="Once delivered, this is what the consumer sees. Notice the gold 'Altara-Verified' chip next to the document — click it to go straight to the certificate." highlight />
          <DemoLink n={4} icon={ExternalLink} url={scenario.demo_links.public_verify_page}
            title="The public verify page (the crown jewel)"
            body="Anyone with the ID can verify the certificate. Signature-valid badge auto-populates. Drop a file to compare SHA-256. Downloadable PDF and signed JSON." highlight />
          <DemoLink n={5} icon={FileText} url={scenario.demo_links.trust_certificate_pdf}
            title="The certificate PDF (for compliance filing)"
            body="One-page A4 with QR code that reopens the verify page. Downloadable for the advisor's records and any regulator." />
          <DemoLink n={6} icon={KeyRound} url={scenario.demo_links.public_key_jwks}
            title="Altara's public signing key (JWKS)"
            body="Independently verifiable — advanced users can re-verify the Ed25519 signature offline using any standard crypto library." />
          <DemoLink n={7} icon={ShieldCheck} url={scenario.demo_links.consumer_wallet_safety_verify}
            title="The consumer verifies a random document"
            body="'My Digital Safety' → drag any file to check whether Altara issued a certificate for it. Show the green MATCH state with the demo quote PDF; then a red MISMATCH with a random file." />
        </div>
      </div>

      {/* Copy the whole bundle */}
      <CopyBundleCard scenario={scenario} />
    </section>
  );
}

function ActorCard({ icon: Icon, title, subtitle, rows, tone }) {
  const bg = tone === "gold" ? `${GOLD}20` : `${NAVY}10`;
  return (
    <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-5">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: bg, color: NAVY }}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[15px] font-semibold" style={{ color: NAVY }}>{title}</div>
          <div className="text-[11px] text-slate-500">{subtitle}</div>
        </div>
      </div>
      <div className="mt-4 space-y-2">
        {rows.map(([k, v]) => (
          <div key={k} className="grid grid-cols-[110px_1fr] gap-3 text-[12px]">
            <span className="uppercase tracking-widest text-[10px] text-slate-500">{k}</span>
            <span className="font-mono text-slate-700 break-all">{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function DemoLink({ n, icon: Icon, url, title, body, highlight }) {
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      data-testid={`demo-link-${n}`}
      className={`flex items-start gap-4 rounded-2xl p-5 border transition hover:shadow-md ${highlight ? "shadow-md" : ""}`}
      style={{
        background: highlight ? "white" : "white",
        borderColor: highlight ? GOLD : "#E2E8F0",
      }}
    >
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-white text-sm font-bold" style={{ background: NAVY }}>{n}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 flex-shrink-0" style={{ color: highlight ? GOLD : NAVY }} />
          <span className="text-[14px] font-semibold" style={{ color: NAVY }}>{title}</span>
        </div>
        <p className="mt-1 text-[12px] leading-relaxed text-slate-600">{body}</p>
        <div className="mt-2 font-mono text-[10px] text-slate-400 truncate">{url}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-400 mt-2 flex-shrink-0" />
    </a>
  );
}

function CopyBundleCard({ scenario }) {
  const [copied, setCopied] = useState(false);
  const bundle = JSON.stringify({
    scenario_id: scenario.scenario_id,
    consumer: scenario.consumer,
    advisor: scenario.advisor,
    trust_certificate: scenario.trust_certificate,
    demo_links: scenario.demo_links,
  }, null, 2);
  const copy = async () => {
    try { await navigator.clipboard.writeText(bundle); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5">
      <div className="flex items-center gap-2 mb-3">
        <CheckCircle2 className="w-4 h-4" style={{ color: GOLD }} />
        <span className="text-[13px] font-semibold" style={{ color: NAVY }}>Copy the whole bundle</span>
        <button
          onClick={copy}
          data-testid="demo-copy-bundle"
          className="ml-auto inline-flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-medium border"
          style={{ borderColor: `${NAVY}22`, color: NAVY }}
        >
          <Copy className="w-3 h-3" /> {copied ? "Copied" : "Copy JSON"}
        </button>
      </div>
      <pre className="text-[10px] font-mono text-slate-700 bg-slate-50 rounded-lg p-3 overflow-x-auto max-h-[280px]">{bundle}</pre>
    </div>
  );
}

function HowItProves() {
  const rows = [
    { title: "Origin", body: "The delivery source (consumer-led drop-off or banker push), depositor name, email and institution — all pinned into the signed payload." },
    { title: "Integrity", body: "The document's SHA-256 lives inside the signed payload. Change a single byte of the file and the verify page reports MISMATCH." },
    { title: "Version", body: "Each revision includes prev_hash (SHA-256 of the previous certificate) and supersedes[]. Revisions can't be silently reordered or dropped." },
    { title: "Timing", body: "Server-issued delivered_at + issued_at inside the signed payload. Certificates cannot be backdated." },
    { title: "Non-repudiation", body: "Ed25519 signature by Altara's key. Verifiable offline against the published JWKS. Anyone can independently reproduce the check." },
  ];
  return (
    <section>
      <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: GOLD }}>What the certificate actually proves</div>
      <h2 className="font-display text-2xl lg:text-3xl font-semibold mt-1" style={{ color: NAVY }}>Five layers of cryptographic assurance</h2>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        {rows.map(r => (
          <div key={r.title} className="rounded-2xl bg-white border border-slate-100 p-5 shadow-sm">
            <div className="font-semibold text-sm" style={{ color: NAVY }}>{r.title}</div>
            <p className="mt-2 text-[12px] leading-relaxed text-slate-600">{r.body}</p>
          </div>
        ))}
      </div>
      <div className="mt-4 rounded-xl p-4 border" style={{ background: "#FEF3C7", borderColor: "#FCD34D" }}>
        <div className="text-[12px] text-amber-900 leading-relaxed">
          <strong>Honest MVP note:</strong> depositor identity is self-declared in this phase — the certificate honestly labels it <code>identity_verified: false</code>. Phase 2 layers DKIM checks + institution-issued sub-signatures for a fully-verified identity chain.
        </div>
      </div>
    </section>
  );
}
